import { Chat, Message } from "../types/conversations";
import { User } from "../types";
import api from "./api";
import Strings from "../constants/Strings";

// Função auxiliar para obter o ID do usuário atual do token
function getCurrentUserId(): number | null {
  try {
    const token = localStorage.getItem(Strings.token_jwt);
    if (!token) {
      console.warn("Token não encontrado no localStorage");
      return null;
    }

    // Decodifica o token JWT (formato básico, sem biblioteca externa)
    const payload = JSON.parse(atob(token.split(".")[1]));

    // Tenta diferentes campos possíveis para o ID
    const userId =
      payload.id || payload.user_id || payload.userId || payload._id;

    if (userId) {
      const userIdNum = Number(userId);
      if (!Number.isNaN(userIdNum)) {
        return userIdNum;
      }
    }

    // Log para debug
    console.warn("ID do usuário não encontrado no token. Payload:", payload);
    return null;
  } catch (error) {
    console.error("Erro ao decodificar token:", error);
    return null;
  }
}

// ============= SERVICE METHODS =============

async function getAllChats(
  limit: number = 50,
  offset: number = 0,
): Promise<{
  chats: Chat[];
  total: number;
  has_more: boolean;
  offset: number;
  limit: number;
}> {
  try {
    const response = await api.get("/conversations/chats/", {
      params: { limit, offset },
    });

    // Suporta tanto o formato antigo (array direto) quanto o novo (objeto com paginação)
    let chatsData: Chat[];
    let total = 0;
    let has_more = false;

    if (Array.isArray(response.data)) {
      // Formato antigo (compatibilidade)
      chatsData = response.data;
      total = chatsData.length;
      has_more = false;
    } else if (response.data.chats) {
      // Formato novo com paginação
      chatsData = response.data.chats;
      total = response.data.total || chatsData.length;
      has_more = response.data.has_more || false;
    } else {
      chatsData = [];
    }

    // Converter timestamps para Date
    const chats = chatsData.map((chat: Chat) => ({
      ...chat,
      lastSeen: chat.lastSeen ? new Date(chat.lastSeen) : null,
    }));

    return {
      chats,
      total,
      has_more,
      offset,
      limit,
    };
  } catch (error) {
    console.error("Erro ao buscar chats:", error);
    return {
      chats: [],
      total: 0,
      has_more: false,
      offset,
      limit,
    };
  }
}

async function getChatById(chatId: number): Promise<Chat | undefined> {
  try {
    const result = await getAllChats(100, 0); // Busca mais chats para encontrar o específico
    return result.chats.find((chat) => chat.id === chatId);
  } catch (error) {
    console.error("Erro ao buscar chat:", error);
    return undefined;
  }
}

async function getChatsBySearch(query: string): Promise<Chat[]> {
  try {
    const response = await api.get(
      `/conversations/chats/search?q=${encodeURIComponent(query)}`,
    );
    // Converter timestamps para Date
    const chats = response.data.map((chat: Chat) => ({
      ...chat,
      lastSeen: chat.lastSeen ? new Date(chat.lastSeen) : null,
    }));
    return chats;
  } catch (error) {
    console.error("Erro ao buscar chats:", error);
    return [];
  }
}

async function getMessagesByChatId(
  chatId: number,
  limit: number = 50,
  offset: number = 0,
): Promise<{
  messages: Message[];
  total: number;
  has_more: boolean;
  offset: number;
  limit: number;
}> {
  try {
    const response = await api.get(`/conversations/${chatId}/messages`, {
      params: { limit, offset },
    });

    // Suporta tanto o formato antigo (array direto) quanto o novo (objeto com paginação)
    let messagesData: Message[];
    let total = 0;
    let has_more = false;

    if (Array.isArray(response.data)) {
      // Formato antigo (compatibilidade)
      messagesData = response.data;
      total = messagesData.length;
      has_more = false;
    } else if (response.data.messages) {
      // Formato novo com paginação
      messagesData = response.data.messages;
      total = response.data.total || messagesData.length;
      has_more = response.data.has_more || false;
    } else {
      messagesData = [];
    }

    // Converter timestamps para Date com validação
    const messages = messagesData.map((message: Message) => {
      let timestamp: Date;
      if (message.timestamp) {
        timestamp = new Date(message.timestamp);
        // Verificar se a data é válida
        if (isNaN(timestamp.getTime())) {
          console.warn("Timestamp inválido recebido:", message.timestamp);
          timestamp = new Date(); // Usar data atual como fallback
        }
      } else {
        timestamp = new Date(); // Usar data atual se não houver timestamp
      }

      return {
        ...message,
        timestamp,
      };
    });

    return {
      messages,
      total,
      has_more,
      offset,
      limit,
    };
  } catch (error) {
    console.error("Erro ao buscar mensagens:", error);
    return {
      messages: [],
      total: 0,
      has_more: false,
      offset,
      limit,
    };
  }
}

interface MessagePayload {
  content: string;
  type: "text" | "image" | "file" | "treino";
  fileUrl?: string;
  fileName?: string;
  fileSize?: string;
  exercise_id?: number;
}

async function addMessageToChat(
  chatId: number,
  message: Omit<Message, "id" | "timestamp">,
): Promise<Message | null> {
  try {
    // Preparar dados para envio
    const messageData: MessagePayload = {
      content: message.content || "",
      type: message.type || "text",
    };

    // Adicionar campos de arquivo se existirem
    if (message.fileUrl) {
      messageData.fileUrl = message.fileUrl;
    }
    if (message.fileName) {
      messageData.fileName = message.fileName;
    }
    if (message.fileSize) {
      messageData.fileSize = message.fileSize;
    }

    console.log("Enviando mensagem:", messageData);
    console.log("Chat ID:", chatId);

    const response = await api.post(
      `/conversations/${chatId}/messages/`,
      messageData,
    );

    console.log("Resposta do servidor ao criar mensagem:", response.data);
    console.log("Status da resposta:", response.status);

    if (!response || !response.data) {
      console.error("Resposta inválida do servidor:", response);
      return null;
    }

    // Se a resposta vier como array, isso significa que o backend retornou todas as mensagens
    // Nesse caso, devemos encontrar a mensagem recém-criada
    let receivedMessage = response.data;
    if (Array.isArray(response.data)) {
      if (response.data.length === 0) {
        console.error("Array de mensagens vazio na resposta");
        return null;
      }
      console.log("Resposta era array com", response.data.length, "mensagens");
      console.log("Conteúdo enviado:", messageData.content);
      console.log(
        "Últimas 5 mensagens do array:",
        response.data
          .slice(-5)
          .map((m) => ({ id: m.id, content: m.content?.substring(0, 50) })),
      );

      // Primeiro, tentar encontrar a mensagem pelo conteúdo (mais confiável)
      // Normalizar espaços em branco para comparação
      const normalizedSentContent = (messageData.content || "").trim();
      const matchingByContent = response.data.find((msg) => {
        const msgContent = (msg.content || "").trim();
        return (
          msgContent === normalizedSentContent ||
          msgContent.toLowerCase() === normalizedSentContent.toLowerCase()
        );
      });

      if (matchingByContent) {
        console.log(
          "Mensagem encontrada pelo conteúdo (ID:",
          matchingByContent.id || matchingByContent._id,
          ", conteúdo:",
          matchingByContent.content?.substring(0, 50),
          "):",
          matchingByContent,
        );
        receivedMessage = matchingByContent;
      } else {
        // Se a mensagem enviada não está no array, significa que o backend retornou mensagens antigas
        // Nesse caso, retornamos null para que o componente recarregue as mensagens do servidor
        console.warn(
          "Mensagem enviada ('" +
            messageData.content +
            "') não encontrada no array retornado. " +
            "O backend provavelmente retornou mensagens antigas. " +
            "Retornando null para recarregar mensagens do servidor.",
        );
        return null;
      }
    }

    // Garantir que senderId é um número
    // Verificar múltiplas possibilidades: senderId, sender_id, ou sender.id
    const senderIdRaw =
      receivedMessage.senderId ||
      receivedMessage.sender_id ||
      (receivedMessage.sender && typeof receivedMessage.sender === "object"
        ? receivedMessage.sender.id
        : null) ||
      (receivedMessage.sender && typeof receivedMessage.sender === "number"
        ? receivedMessage.sender
        : null);

    console.log(
      "SenderId extraído da resposta:",
      senderIdRaw,
      "Tipo:",
      typeof senderIdRaw,
    );
    console.log("Objeto sender completo:", receivedMessage.sender);

    let senderIdNum: number;
    if (senderIdRaw !== undefined && senderIdRaw !== null) {
      senderIdNum = Number(senderIdRaw);
      if (isNaN(senderIdNum)) {
        console.warn("SenderId não é um número válido, usando currentUserId");
        const currentUserId = getCurrentUserId();
        if (!currentUserId) {
          console.error("Não foi possível determinar o senderId");
          return null;
        }
        senderIdNum = currentUserId;
      }
    } else {
      console.warn("SenderId não encontrado na resposta, usando currentUserId");
      const currentUserId = getCurrentUserId();
      if (!currentUserId) {
        console.error("Não foi possível determinar o senderId");
        return null;
      }
      senderIdNum = currentUserId;
    }

    // Garantir que temos um ID válido
    const messageId = receivedMessage.id || receivedMessage._id;
    if (messageId === undefined || messageId === null || messageId === "") {
      console.error(
        "ID da mensagem não encontrado na resposta:",
        receivedMessage,
      );
      return null;
    }

    // Converter timestamp para Date com validação
    let timestamp: Date;
    if (receivedMessage.timestamp) {
      const timestampValue = receivedMessage.timestamp;

      timestamp = new Date(timestampValue);

      // Verificar se a data é válida
      if (isNaN(timestamp.getTime())) {
        console.error("Timestamp inválido recebido:", timestampValue);
        timestamp = new Date(); // Usar data atual como fallback
      }
    } else {
      console.warn("Nenhum timestamp recebido, usando data atual");
      timestamp = new Date(); // Usar data atual se não houver timestamp
    }

    // Garantir que chatId é um número
    const chatIdRaw = receivedMessage.chatId || receivedMessage.chat_room_id;
    const chatIdNum =
      chatIdRaw !== undefined && chatIdRaw !== null
        ? Number(chatIdRaw)
        : chatId;

    const result: Message = {
      id: messageId,
      chatId: chatIdNum,
      senderId: senderIdNum,
      content: receivedMessage.content || "",
      type: receivedMessage.type || "text",
      fileUrl: receivedMessage.fileUrl || receivedMessage.file_url,
      fileName: receivedMessage.fileName || receivedMessage.file_name,
      fileSize: receivedMessage.fileSize || receivedMessage.file_size,
      timestamp,
      status: receivedMessage.status || "sent",
      exercise: receivedMessage.exercise,
      exerciseId: receivedMessage.exerciseId || receivedMessage.exercise_id,
    };

    console.log("Mensagem processada:", result);
    console.log(
      "SenderId da mensagem:",
      result.senderId,
      "Tipo:",
      typeof result.senderId,
    );
    console.log(
      "CurrentUserId:",
      getCurrentUserId(),
      "Tipo:",
      typeof getCurrentUserId(),
    );

    return result;
  } catch (error: unknown) {
    console.error("Erro ao enviar mensagem:", error);
    if (error && typeof error === "object" && "response" in error) {
      const apiError = error as {
        response?: { data?: unknown; status?: number };
      };
      console.error("Status do erro:", apiError.response?.status);
      console.error("Dados do erro:", apiError.response?.data);
    }
    return null;
  }
}

async function getChatStudent(chatId: number): Promise<User | null> {
  try {
    const response = await api.get(`/conversations/${chatId}/student`);
    return response.data;
  } catch (error) {
    console.error("Erro ao buscar aluno do chat:", error);
    return null;
  }
}

async function linkStudentToChat(
  chatId: number,
  password: string,
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await api.post(`/conversations/${chatId}/link-student/`, {
      password,
    });
    if (response.data?.success) {
      return { success: true };
    }
    return { success: false, error: response.data?.error ?? "Erro ao vincular." };
  } catch (error: unknown) {
    const message =
      error &&
      typeof error === "object" &&
      "response" in error &&
      error.response &&
      typeof error.response === "object" &&
      "data" in error.response &&
      error.response.data &&
      typeof error.response.data === "object" &&
      "error" in error.response.data &&
      typeof (error.response.data as { error: string }).error === "string"
        ? (error.response.data as { error: string }).error
        : "Erro ao vincular. Tente novamente.";
    return { success: false, error: message };
  }
}

async function uploadMessageFile(
  file: File,
): Promise<{ url: string; fileName: string; originalName: string } | null> {
  try {
    // Converte arquivo para base64
    const reader = new FileReader();

    return new Promise((resolve, reject) => {
      reader.onload = async () => {
        try {
          const base64String = reader.result as string;

          const response = await api.post("/conversations/upload-file/", {
            base64: base64String,
            file_name: file.name,
          });

          if (response.data && response.data.url) {
            resolve({
              url: response.data.url,
              fileName: response.data.file_name,
              originalName: response.data.original_name || file.name,
            });
          } else {
            reject(new Error("Resposta inválida do servidor"));
          }
        } catch (error) {
          console.error("Erro ao fazer upload do arquivo:", error);
          reject(error);
        }
      };

      reader.onerror = () => {
        reject(new Error("Erro ao ler arquivo"));
      };

      reader.readAsDataURL(file);
    });
  } catch (error) {
    console.error("Erro ao fazer upload do arquivo:", error);
    return null;
  }
}

export interface ChatStats {
  kpis: {
    total_sent: number;
    total_received: number;
    delivered: number;
    read: number;
  };
  chart_data: {
    date: string;
    enviadas: number;
    recebidas: number;
  }[];
}

async function getChatStats(days: number = 7): Promise<ChatStats | null> {
  try {
    const response = await api.get(`/conversations/stats/?days=${days}`);
    return response.data;
  } catch (error) {
    console.error("Erro ao buscar estatísticas do chat:", error);
    return null;
  }
}

const conversations = {
  getAllChats,
  getChatById,
  getChatsBySearch,
  getMessagesByChatId,
  addMessageToChat,
  getCurrentUserId,
  getChatStudent,
  linkStudentToChat,
  uploadMessageFile,
  getChatStats,
};

export default conversations;
